import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, ViewChild } from "@angular/core";

import { v4 as uuidv4 } from "uuid";
import { BehaviorSubject } from "rxjs";

import { WidgetHub } from "@main/reporting.v3/core/widget-hub/models";
import { IEmittedVariable, IStaticVariable, IVariableRule } from "@main/reporting.v3/core/variables/variable-models";
import {
    SequentialTaskHandlerService
} from "@main/reporting.v3/core/concurrency/services/sequential-task-handler.service";
import { LatestTaskHandlerService } from "@main/reporting.v3/core/concurrency/services/latest-task-handler.service";
import { WidgetOutputMode, PdfExportSettings } from "@main/reporting.v3/core/reporting-models";
import { VariableRuleService } from "@main/reporting.v3/core/variables/variable-rule.service";
import { VariableHubService } from "@main/reporting.v3/core/variables/variable-hub.service";
import { IVariableReceiver } from "@main/reporting.v3/core/variables/variable-receiver";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";

import {
    WidgetHtmlExpressionsResolverService
} from "@main/reporting.v3/widget.html/services/widget-html-expressions-resolver.service";
import {
    WidgetHtmlContentProcessorService
} from "@main/reporting.v3/widget.html/services/widget-html-content-processor.service";
import {
    WidgetHtmlEmbeddedWidgetsService
} from "@main/reporting.v3/widget.html/services/widget-html-embedded-widgets.service";
import {
    WidgetHtmlPdfExportingApiService
} from "@main/reporting.v3/widget.html/api/widget-html-pdf-exporting-api.service";
import {
    WidgetHtmlPdfExportingService
} from "@main/reporting.v3/widget.html/services/widget-html-pdf-exporting.service";
import {
    HtmlForLoopDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-for-loop-directive.service";
import {
    HtmlWidgetDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-widget-directive.service";
import {
    HtmlEvalDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-eval-directive.service";
import {
    HtmlRangeDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-range-directive.service";
import {
    HtmlQueryDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-query-directive.service";
import {
    HtmlIndexDirectiveService
} from "@main/reporting.v3/widget.html/services/html-expressions/html-directive-evaluators/html-index-directive.service";
import { HtmlContext, HtmlCssClass, HtmlPdfContext } from "@main/reporting.v3/widget.html/models";
import { WidgetHtmlApiService } from "@main/reporting.v3/widget.html/api/widget-html-api.service";
import { IHtmlViewDTO } from "@main/reporting.v3/widget.html/widget-html.models";
import {
    queryResolverProvider
} from "@main/reporting.v3/core/query-hub/constants/query-resolver-provider.constants";
import {
    widgetResolverProvider
} from "@main/reporting.v3/core/widget-hub/constants/widget-resolver-provider.constants";
import {
    widgetHubProvider
} from "@main/reporting.v3/core/widget-hub/constants/widget-hub-provider.constants";
import {
    queryHubProvider
} from "@main/reporting.v3/core/query-hub/constants/query-hub-provider.constants";
import {
    WIDGET_HUB
} from "@main/reporting.v3/core/widget-hub/constants/widget-hub.constants";


@Component({
    selector: "widget-html",
    templateUrl: "./widget-html.component.html",
    styleUrls: ["./widget-html.component.scss"],
    providers: [
        WidgetHtmlExpressionsResolverService,
        WidgetHtmlContentProcessorService,
        WidgetHtmlEmbeddedWidgetsService,
        WidgetHtmlPdfExportingService,
        SequentialTaskHandlerService,
        HtmlForLoopDirectiveService,
        HtmlWidgetDirectiveService,
        HtmlRangeDirectiveService,
        HtmlIndexDirectiveService,
        HtmlQueryDirectiveService,
        HtmlEvalDirectiveService,
        LatestTaskHandlerService,
        widgetResolverProvider,
        queryResolverProvider,
        widgetHubProvider,
        queryHubProvider
    ]
})
export class WidgetHtmlComponent implements IWidgetComponent, IVariableReceiver, IPdfExportable, OnDestroy {
    // Widget settings.
    public readonly htmlContentClass: string = HtmlCssClass.WidgetContent;
    public readonly widgetSelector: string = "widget-html";
    public readonly widgetType: string = "html";

    // Variable handling settings.
    public readonly variableReceiverId: string = uuidv4();

    // CSS injection settings.
    public readonly cssInjectionScopeId: string = uuidv4();

    // TODO: Temporary approach until we implement a widget store.
    private widgetHub: WidgetHub = inject(WIDGET_HUB);

    // Loading settings.
    public pdfExportingLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    // Component fields.
    protected widgetTitle: string;

    private emittedVariables: IEmittedVariable[] = [];

    private _htmlView: IHtmlViewDTO;
    private _widgetSourceId: string;
    private _widgetVisible: boolean = true;
    private _output: WidgetOutputMode = "web";
    private _staticVariables: IStaticVariable[];
    private _htmlContainer: ElementRef<HTMLElement>;

    // Shows or hides exporting button.
    @Input() exportable: boolean = true;

    // Flag to ignore widget visibility rules (for preview purposes).
    @Input() ignoreDisplayRule: boolean = false;

    constructor(private contentProcessorService: WidgetHtmlContentProcessorService,
                private embeddedWidgetsService: WidgetHtmlEmbeddedWidgetsService,
                private pdfExportingApiService: WidgetHtmlPdfExportingApiService,
                private pdfExportingService: WidgetHtmlPdfExportingService,
                private lastTaskHandlerService: LatestTaskHandlerService,
                private variableRuleService: VariableRuleService,
                private widgetHtmlService: WidgetHtmlApiService,
                private variableHubService: VariableHubService,
                private changeDetectorRef: ChangeDetectorRef,
                private elementRef: ElementRef) {
    }

    @ViewChild("htmlContainer") protected set htmlContainer(value: ElementRef<HTMLElement>) {
        this._htmlContainer = value;
        this.refreshHtmlContent();
    }

    get htmlContainer(): ElementRef<HTMLElement> {
        return this._htmlContainer;
    }

    // Determines which HTML code should be used for rendering: web view of PDF exporting view.
    @Input() set output(value: WidgetOutputMode) {
        this._output = value ?? "web";
        this.embeddedWidgetsService.refreshWidgetOutputs(this.output);
    }

    get output(): WidgetOutputMode {
        return this._output;
    }

    // Determines if HTML is able to receive variables.
    @Input() set variableReceiver(value: boolean) {
        if (!!value || value == null) this.variableHubService.registerVariableReceiver(this);
        else this.variableHubService.unregisterVariableReceiver(this);
    }

    // Set of fixed-value variables to consider during HTML rendering.
    @Input() set staticVariables(value: IStaticVariable[]) {
        if (!value) return;
        this._staticVariables = value;
        this.refreshWidgetView();
        this.embeddedWidgetsService.refreshWidgetStaticVariables(this.staticVariables);
    }

    get staticVariables(): IStaticVariable[] {
        return this._staticVariables;
    }

    // HTML view for refreshing purposes.
    @Input() set htmlView(value: IHtmlViewDTO) {
        if (!value) return;

        // TODO: Temporary approach until we implement a widget store.
        this.widgetHub.widgetSourceId = value.widgetSourceId;

        this._htmlView = value;
        this._widgetSourceId = value.widgetSourceId;

        this.refreshWidgetView();
    }

    get htmlView(): IHtmlViewDTO {
        return this._htmlView;
    }

    // Widget source id for refreshing purposes.
    @Input() set widgetSourceId(value: string) {
        if (this.widgetSourceId === value) return;

        // TODO: Temporary approach until we implement a widget store.
        this.widgetHub.widgetSourceId = value;

        this._widgetSourceId = value;

        this.refreshWidget().then();
    }

    get widgetSourceId(): string {
        return this._widgetSourceId;
    }

    // Determine if the widget is visible considering display rule.
    @Input() set widgetVisible(value: boolean) {
        this._widgetVisible = value;
    }

    get widgetVisible(): boolean {
        return this.ignoreDisplayRule || this._widgetVisible;
    }

    get widgetName(): string {
        return this.htmlView?.widgetSource?.name;
    }

    get widgetCode(): string {
        return this.htmlView?.widgetSource?.code;
    }

    get pdfViewVisible(): boolean {
        return this.widgetVisible;
    }

    get widgetContainerClass(): string {
        if (this.output !== "PDF") return HtmlCssClass.WidgetContainer;

        return `${HtmlCssClass.WidgetContainer} ${HtmlCssClass.PdfView}`;
    }


    public ngOnDestroy(): void {
        this.variableHubService.unregisterVariableReceiver(this);
        this.pdfExportingLoading.complete();
    }


    // Variable management methods.
    //TODO: consider further improvement: avoid redundant refreshing widget view on ANY variable received.
    // To do that, on widget init we would need to build a list of widget-related variables from widget's
    // parts, dependent on variables (title, HTML content). Try to simplify the logic of the method.
    public receiveEmittedVariables(variables: IEmittedVariable[]): void {
        // Save current state of variables.
        const oldVariables: IEmittedVariable[] = this.emittedVariables ?? [];

        this.emittedVariables = variables?.filter(variable => !variable.empty);

        if (!this.htmlView) return;

        // Show/hide widget variable.
        const displayRule: IVariableRule = this.htmlView.widgetSource?.displayRule;

        this.widgetVisible = !displayRule || this.variableRuleService.containsMatch(displayRule, variables);

        // Refresh the widget if some related variable changed its value.
        if (!this.variableRuleService.equalVariableSets(this.emittedVariables, oldVariables)) this.refreshWidgetView();
    }

    // Refreshing methods.
    private async refreshWidget(): Promise<void> {
        if (!this.widgetSourceId) return;
        this.htmlView = await this.widgetHtmlService.getView(this.widgetSourceId);
        this.changeDetectorRef.detectChanges();
    }

    private refreshHtmlContent(): void {
        if (!this.htmlView) return;
        if (!this.htmlContainer?.nativeElement) return;

        const html: string = this.htmlView.htmlCode;

        const context: HtmlContext = this.getHtmlContent();

        const container: HTMLElement = this.htmlContainer?.nativeElement;

        this.lastTaskHandlerService.handleTask(async (): Promise<void> => {
            // Process the content avoiding race conditions.
            // If multiple tasks arrive quickly, only the latest one is processed.
            await this.contentProcessorService.processContent(context, html, container);
        }).then();

        // TODO: Get rid of this patch.
        // Refresh widget visibility variable if this is the first time.
        if (this.widgetVisible == null) {
            this.widgetVisible = this.ignoreDisplayRule || !this.htmlView.widgetSource?.displayRule;
        }
    }

    private refreshTitle(): void {
        const title: string = this.htmlView?.widgetSource?.title;
        if (!title?.length) return;

        let processedTitle = this.variableRuleService.embedVariableValues(title, this.emittedVariables);
        this.widgetTitle = this.variableRuleService.cleanupUnmatchedVariables(processedTitle);
    }

    private refreshWidgetView(): void {
        this.refreshHtmlContent();
        this.refreshTitle();
    }

    // Auxiliary methods.
    private getHtmlContent(): HtmlContext {
        return {
            widgetOutput: this.output,
            cssRules: this.htmlView?.cssRules,
            staticVariables: this.staticVariables,
            emittedVariables: this.emittedVariables,
            cssInjectionScopeId: this.cssInjectionScopeId,
        }
    }

    private getHtmlPdfContext(): HtmlPdfContext {
        const context: HtmlContext = this.getHtmlContent();

        return {
            ...context,
            widgetName: this.widgetName,
            template: this.elementRef?.nativeElement?.innerHTML,
        }
    }

    public async getPdfConfiguration(): Promise<JsReportConfiguration> {
        const context: HtmlPdfContext = this.getHtmlPdfContext();

        return await this.pdfExportingService.getPdfConfiguration(context);
    }

    public async generatePdf(): Promise<void> {
        if (this.pdfExportingLoading.value) return;

        try {
            this.pdfExportingLoading.next(true);

            const context: HtmlPdfContext = this.getHtmlPdfContext();
            const settings: PdfExportSettings = await this.pdfExportingService.getPdfExportSettings(context);

            await this.pdfExportingApiService.requestPdfExport(this.widgetSourceId, settings);

        } finally {
            this.pdfExportingLoading.next(false);
        }
    }
}